/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Box, Card, Text, VStack, Spinner, IconButton, Tooltip, Badge, Flex, Collapse, useToast } from '@chakra-ui/react';
import { FaPlus, FaChevronDown } from 'react-icons/fa6';
import { useQueryClient } from 'react-query';
import { DashboardHooks } from '@partiu/api/dashboard/hooks';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider';
import { axiosInstance } from '@partiu/api/axios';
import { FormCreate, ModuleProduct } from '../../ModulesSuperApp/steps/forms/FormCreate';
import { allBenefitsProduct, groupByCategory } from '../../ModulesSuperApp/steps/forms/BenefitsList';
import { BenefitCategoryPanel, Benefit as BenefitItem } from '../../ModulesSuperApp/components/BenefitCategory';

interface Step3SVAListProps {
  products: ModuleProduct[];
  isLoading?: boolean;
  refetch: () => void;
  createRef?: React.RefObject<{
    open: (data?: ModuleProduct, options?: { showBenefitsSelector?: boolean; benefit?: any }) => void;
    close: () => void;
  } | null>;
}


export const Step3SVAList: React.FC<Step3SVAListProps> = ({ isLoading, createRef, products, refetch }) => {
  const { colors } = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isHovered, setIsHovered] = React.useState(false);
  const [svaCategories, setSvaCategories] = React.useState<{ category: string; benefits: BenefitItem[] }[]>([]);
  const [expandedCategories, setExpandedCategories] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    // Mapear produtos existentes para o formato de benefícios (usa active real da API)
    const existingBenefits: BenefitItem[] = (products || []).map((product) => ({
      id: product.id?.toString(),
      title: product.name || product.title,
      image: (product.metadata?.logo?.value as string) || product.icon || '',
      category: product.metadata?.category?.value || product.category || 'Outros',
      active: product.active !== false,
      disabled: false,
      allowEdit: true,
    }));

    // Benefícios padrão (catálogo de SVAs)
    const defaultBenefits: BenefitItem[] = allBenefitsProduct.map((benefit) => ({
      ...benefit,
      active: false,
      disabled: false,
    }));

    // Combinar e remover duplicados por título
    const combined = [...existingBenefits, ...defaultBenefits];
    const unique = combined.reduce<BenefitItem[]>((acc, current) => {
      if (!acc.some((item) => item.title.toLowerCase() === current.title.toLowerCase())) {
        acc.push(current);
      }
      return acc;
    }, []);

    const grouped = groupByCategory(unique as any);
    setSvaCategories(grouped as any);
    setExpandedCategories((prev) => {
      const next: Record<string, boolean> = {};
      grouped.forEach((group) => {
        next[group.category] = prev[group.category] ?? true;
      });
      return next;
    });
  }, [products]);

  const productsByName = React.useMemo(() => {
    const map = new Map<string, ModuleProduct>();
    (products || []).forEach((product) => {
      const key = (product.name || product.title || '').toLowerCase();
      if (key) {
        map.set(key, product);
      }
    });
    return map;
  }, [products]);

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  const handleBenefitSelect = (benefit: BenefitItem, event?: React.MouseEvent) => {
    event?.stopPropagation();

    const key = benefit.title.toLowerCase();
    const existingProduct = productsByName.get(key);

    if (existingProduct && createRef?.current) {
      createRef.current.open(existingProduct, { showBenefitsSelector: false });
      return;
    }

    if (createRef?.current) {
      createRef.current.open(undefined, {
        showBenefitsSelector: false,
        benefit: {
          title: benefit.title,
          category: benefit.category,
          image: benefit.image,
          url: benefit.url,
        },
      });
    }
  };

  const handleAddNew = (category?: string) => {
    if (!createRef?.current) return;

    if (category) {
      createRef.current.open(
        {
          sectionTitle: category,
        } as ModuleProduct,
        { showBenefitsSelector: false }
      );
      return;
    }

    createRef.current.open();
  };

  const [togglingId, setTogglingId] = React.useState<string | null>(null);

  const handleBenefitToggle = async (benefit: BenefitItem) => {
    if (!benefit.id) {
      toast({
        title: 'Adicione o módulo primeiro para poder habilitar ou desabilitar',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setTogglingId(benefit.id);
      const newActive = !benefit.active;
      await axiosInstance.patch(`/product_staff/${benefit.id}/`, { active: newActive });
      await queryClient.refetchQueries([DashboardHooks.Products]);
      await refetch();
      toast({
        title: newActive ? 'Módulo habilitado' : 'Módulo desabilitado',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar módulo',
        description: error?.response?.data?.detail || 'Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <VStack spacing={3} width="100%" position="relative">
      {/* Botão Flutuante para Adicionar Novo Produto SVA */}
      <Tooltip
        label="Adicionar Novo Produto SVA"
        placement="left"
        hasArrow
        bg={colors.primaryColor}
        color="white"
        fontSize="sm"
        fontWeight="semibold"
        px={3}
        py={2}
        borderRadius="lg"
        openDelay={500}
      >
        <Box
          position="fixed"
          bottom="30px"
          right="30px"
          zIndex={1000}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <IconButton
            aria-label="Adicionar novo produto SVA"
            icon={<FaPlus />}
            colorScheme="blue"
            size="lg"
            borderRadius="full"
            boxShadow="xl"
            onClick={() => handleAddNew()}
            css={{
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { transform: 'scale(1)' }
              }
            }}
            _hover={{
              transform: 'scale(1.15)',
              boxShadow: `0 0 25px ${colors.primaryColor}80, 0 0 35px ${colors.primaryColor}60`,
              bg: colors.primaryColor,
            }}
            _active={{
              transform: 'scale(0.95)',
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            bg={colors.primaryColor}
            color="white"
            minW="60px"
            minH="60px"
            fontSize="20px"
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              right: '-2px',
              bottom: '-2px',
              bg: `${colors.primaryColor}40`,
              borderRadius: 'full',
              zIndex: -1,
              opacity: isHovered ? 0.6 : 0.3,
              animation: isHovered ? 'glow 1.5s infinite' : 'none',
              '@keyframes glow': {
                '0%': { boxShadow: `0 0 5px ${colors.primaryColor}50` },
                '50%': { boxShadow: `0 0 20px ${colors.primaryColor}80, 0 0 30px ${colors.primaryColor}60` },
                '100%': { boxShadow: `0 0 5px ${colors.primaryColor}50` }
              },
            }}
          />

          {/* Badge de notificação */}
          <Badge
            position="absolute"
            top="-8px"
            right="-8px"
            bg="red.500"
            color="white"
            borderRadius="full"
            fontSize="xs"
            fontWeight="bold"
            minW="20px"
            minH="20px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            css={{
              animation: 'pulse 1s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { transform: 'scale(1)' }
              }
            }}
            boxShadow="0 0 10px rgba(239, 68, 68, 0.6)"
          >
            !
          </Badge>
        </Box>
      </Tooltip>


      <Card
        height="100%"
        padding={{ base: 3, md: 4 }}
        width="100%"
        flex={{ base: '1', lg: '1' }}
        overflow="hidden"
      >
        <Box
          height="100%"
          width="100%"
          display="flex"
          flexDirection="column"
          overflowY="auto"
          gap={{ base: '4px', md: '8px' }}
        >
          <Box
            bg="purple.50"
            p={3}
            borderRadius="md"
            borderLeft="4px solid"
            borderLeftColor="purple.400"
            mb={3}
            textAlign="center"
          >
            <Text fontSize="14px" lineHeight="1.5" color="gray.700" mb={2}>
              Configure todos os produtos SVAs em um único local:
            </Text>
          </Box>

          <Box marginTop="20px">
            {svaCategories.length > 0 ? (
              <VStack width="100%" spacing={4}>
                {svaCategories.map((category) => {
                  const isOpen = expandedCategories[category.category] ?? true;
                  return (
                    <Box
                      key={category.category}
                      width="100%"
                      border="1px solid"
                      borderColor="gray.100"
                      borderRadius="md"
                      bg="white"
                      boxShadow="sm"
                    >
                      <Flex
                        align="center"
                        justify="space-between"
                        px={4}
                        py={3}
                        cursor="pointer"
                        onClick={() =>
                          setExpandedCategories((prev) => ({
                            ...prev,
                            [category.category]: !isOpen,
                          }))
                        }
                      >
                        <Text fontWeight="bold" fontSize="md">
                          {category.category}
                        </Text>
                        <Box
                          as={FaChevronDown}
                          boxSize={5}
                          transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                          transition="transform 0.2s ease"
                        />
                      </Flex>

                      <Collapse in={isOpen} animateOpacity>
                        <Box px={4} pb={4}>
                          <BenefitCategoryPanel
                            category={category}
                            onBenefitSelect={handleBenefitSelect}
                            onBenefitEdit={(b) => {
                              const prod = productsByName.get(b.title.toLowerCase());
                              if (prod && createRef?.current) createRef.current.open(prod, { showBenefitsSelector: false });
                            }}
                            onBenefitToggle={handleBenefitToggle}
                            onAddNew={handleAddNew}
                            showAddButton
                            cardProps={(benefit) => ({
                              editable: !!benefit.id,
                              small: true,
                              removeCheckbox: !benefit.id,
                              isLoading: benefit.id === togglingId,
                            })}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </VStack>
            ) : (
              <Box>
                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                  Nenhum SVA encontrado, clique no botão flutuante para adicionar um novo SVA
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </Card>

      {createRef && (
        <FormCreate
          createRef={createRef}
          refetch={refetch}
          existingModules={products}
        />
      )}
    </VStack>
  );
};

export default Step3SVAList;
